import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Footerdemo } from '../ui/footer-section';
import { BookOpen, ChevronRight, Home, Settings, Pencil, FileText, User, Search, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

type NavItem = {
  title: string;
  href: string;
  icon: React.ReactNode;
  children?: NavItem[];
};

const navigation: NavItem[] = [
  {
    title: 'Getting Started',
    href: '/docs',
    icon: <Home className="w-4 h-4" />,
  },
  {
    title: 'Content Writer',
    href: '/docs/writer',
    icon: <Pencil className="w-4 h-4" />,
    children: [
      { title: 'Basic Options', href: '/docs/writer/basic-options', icon: <ChevronRight className="w-4 h-4" /> },
      { title: 'Marketing Options', href: '/docs/writer/marketing-options', icon: <ChevronRight className="w-4 h-4" /> },
      { title: 'Writing Styles', href: '/docs/writer/writing-styles', icon: <ChevronRight className="w-4 h-4" /> },
      { title: 'AI Models', href: '/docs/writer/ai-models', icon: <ChevronRight className="w-4 h-4" /> },
    ],
  },
  {
    title: 'Blog Posts',
    href: '/docs/posts',
    icon: <FileText className="w-4 h-4" />,
    children: [
      { title: 'Managing Posts', href: '/docs/posts/managing', icon: <ChevronRight className="w-4 h-4" /> },
      { title: 'Export Options', href: '/docs/posts/export', icon: <ChevronRight className="w-4 h-4" /> },
    ],
  },
  {
    title: 'Strike List',
    href: '/docs/strike-list',
    icon: <CheckCircle className="w-4 h-4" />,
  },
  {
    title: 'Account Settings',
    href: '/docs/settings',
    icon: <Settings className="w-4 h-4" />,
  },
  {
    title: 'Subscription Plans',
    href: '/docs/plans',
    icon: <User className="w-4 h-4" />,
  },
];

export default function DocsLayout() {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isSearchFocused, setIsSearchFocused] = React.useState(false);

  const isActive = (href: string) => {
    if (href === '/docs' && location.pathname === '/docs') {
      return true;
    }
    return location.pathname.startsWith(href) && href !== '/docs';
  };

  const isChildActive = (href: string) => {
    return location.pathname === href;
  };

  const [expandedItems, setExpandedItems] = React.useState<string[]>(() => {
    const expanded: string[] = [];
    
    // Automatically expand the parent item of the active page
    for (const item of navigation) {
      if (item.children) {
        if (item.children.some(child => location.pathname === child.href)) {
          expanded.push(item.href);
        }
      }
    }
    
    return expanded;
  });

  const toggleExpand = (href: string) => {
    if (expandedItems.includes(href)) {
      setExpandedItems(expandedItems.filter(item => item !== href));
    } else {
      setExpandedItems([...expandedItems, href]);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-16">
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-64 lg:w-72 border-r bg-gray-50">
          <div className="p-4 lg:p-6 sticky top-16">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-blue-800" />
              <h2 className="text-lg font-semibold">Documentation</h2>
            </div>
            
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search docs..."
                className="pl-10 bg-white"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
              />
              {isSearchFocused && (
                <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-white rounded-md shadow-lg border z-10">
                  <p className="text-xs text-gray-500 p-2">
                    Search functionality coming soon...
                  </p>
                </div>
              )}
            </div>
            
            <nav className="space-y-1">
              {navigation.map((item) => (
                <div key={item.href}>
                  <Link
                    to={item.href}
                    className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md 
                      ${isActive(item.href) ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}
                    onClick={(e) => {
                      if (item.children) {
                        e.preventDefault();
                        toggleExpand(item.href);
                      }
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon}
                      {item.title}
                    </span>
                    {item.children && (
                      <ChevronRight 
                        className={`w-4 h-4 transition-transform ${
                          expandedItems.includes(item.href) ? 'rotate-90' : ''
                        }`} 
                      />
                    )}
                  </Link>
                  
                  {item.children && expandedItems.includes(item.href) && (
                    <div className="pl-4 mt-1 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`flex items-center px-3 py-1.5 text-sm rounded-md 
                            ${isChildActive(child.href) ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-100'}`}
                        >
                          <span className="flex items-center gap-2">
                            {child.icon}
                            {child.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="mt-8 pt-6 border-t">
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <span className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Back to Home
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </aside>
        
        {/* Main content */}
        <main className="flex-1 p-4 md:p-8 overflow-auto bg-white">
          <div className="max-w-4xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
      <Footerdemo />
    </div>
  );
}